import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import {
  Grid,
  Typography,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
} from '@mui/material'
import ErrorIcon from '@mui/icons-material/Error'

import Modal from 'components/Modal/Modal'
import theme from 'lib/theme'
import { Form, Formik } from 'formik'
import { Order } from 'types/api/order'
import { useCancelOrder } from 'hooks/useOrders'
import { useAlert } from 'providers/AlertProvider'

type OrderCancellationModalProps = {
  order: Order
  handleCloseModal: () => void
  modal: { right: boolean }
}

export const OrderCancellationModal = ({
  order,
  handleCloseModal,
  modal,
}: OrderCancellationModalProps) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { mutateAsync, isLoading } = useCancelOrder(order.id as string)
  const { setAlert } = useAlert()

  const numberOfProducts = order.parts.reduce(
    (prev, part) => prev + part.products.length,
    0
  )

  const initialValues = {
    confirm: false,
  }

  const validationSchema = yup.object({
    confirm: yup.boolean().oneOf([true]),
  })

  const handleSubmit = async () => {
    await mutateAsync(
      {},
      {
        onSuccess: () => {
          handleCloseModal()
          setAlert(t('saved'), 'success', {
            vertical: 'bottom',
            horizontal: isMobile ? 'center' : 'left',
          })
        },
        onError: () => {
          setAlert(t('failureMessage'), 'failure', {
            vertical: 'bottom',
            horizontal: isMobile ? 'center' : 'right',
          })
        },
      }
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async () => handleSubmit()}>
      {({ isValid, dirty, values, setFieldValue, submitForm }) => (
        <Modal
          showButton
          buttonIsDisabled={!isValid || !dirty}
          onButtonClick={submitForm}
          isLoadingRequest={isLoading}
          setModal={handleCloseModal}
          buttonText={t('orderProduct.cancelOrderButton')}
          modal={modal}
          buttonVariant='alert'
          title={t('orderProduct.cancelOrder')}>
          <Box
            display='flex'
            flexDirection='column'
            justifyContent='space-between'
            sx={{ position: 'relative' }}>
            <Box>
              <Grid
                container
                sx={{
                  borderBottom: `2px solid ${theme.palette.action.disabled}`,
                  paddingBottom: theme.spacing(1),
                  marginBottom: theme.spacing(1),
                }}>
                <Grid item sm={6} xs={3}>
                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.text.secondary }}>
                    {t('order').toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item sm={2} xs={3}>
                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.text.secondary }}>
                    {t('orderInfo.products').toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item sm={2} xs={3}>
                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.text.secondary }}>
                    {t('orderProduct.payment').toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item sm={2} xs={3}>
                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.text.secondary }}>
                    {t('orderProduct.charged').toUpperCase()}
                  </Typography>
                </Grid>
              </Grid>
              {order && (
                <Grid container key={order.id} pb={2} mb={3}>
                  <Grid item sm={6} xs={3}>
                    <Typography>#{order.visibleId}</Typography>
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                      {order.customerAddress.name}
                    </Typography>
                  </Grid>
                  <Grid item sm={2} xs={3}>
                    <Typography>{numberOfProducts}</Typography>
                  </Grid>
                  <Grid item sm={2} xs={3}>
                    <Typography>{order.payment.type}</Typography>
                  </Grid>
                  <Grid item sm={2} xs={3}>
                    <Typography>{order.totalPrice.totalPriceVat}</Typography>
                  </Grid>
                </Grid>
              )}
            </Box>

            <Form
              style={{
                width: '100%',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  marginBottom: '10px',
                }}>
                <Box>
                  <Box
                    display='flex'
                    alignItems='center'
                    sx={{ marginLeft: '6px', marginBottom: theme.spacing(1) }}>
                    <ErrorIcon
                      sx={{
                        fill: theme.palette.error.main,
                        '&:hover': { fill: theme.palette.error.main },
                      }}
                    />
                    <Typography variant='body2' ml={1} sx={{ lineHeight: 0 }}>
                      {t('cannotBeUndone')}
                    </Typography>
                  </Box>
                  <FormGroup
                    sx={{
                      marginLeft: theme.spacing(1),
                      marginBottom: theme.spacing(1),
                    }}>
                    <FormControlLabel
                      sx={{ '&.MuiTypography-root': { fontWeight: 'bold' } }}
                      control={
                        <Checkbox
                          data-testid='confirmationCheckbox'
                          sx={{
                            '&.Mui-checked .MuiSvgIcon-root': {
                              fill: theme.palette.primary.main,
                            },
                          }}
                          onChange={(event) => {
                            setFieldValue('confirm', event.target.checked)
                          }}
                        />
                      }
                      checked={values.confirm}
                      label={t('orderProduct.cancellationConfirmation')}
                    />
                  </FormGroup>
                </Box>
              </Box>
            </Form>
          </Box>
        </Modal>
      )}
    </Formik>
  )
}
